import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from 'src/app/shared/icons/components/icon/icon.component';
import { SymbolComponent } from 'src/app/shared/icons/components/symbol/symbol.component';
import { defaultDropdownPositionPairs } from 'src/app/shared/overlay/cdk-overlay-position-pairs';
import { OverlayDirective } from 'src/app/shared/overlay/overlay.directive';
import { ScrollFadeDirective } from '../../../../table/directives/scroll-fade.directive';
import { FormatDatePipe } from '../../../../table/pipes/format-date.pipe';
import { GoogleMapsComponent } from '../../../google-maps/google-maps.component';
import { NON_INTERACTIVE_GOOGLE_MAPS_OPTIONS } from '../../../google-maps/model/google-maps-input.interface';
import { FormatNumberPipe } from '../../../pipes/format-number.pipe';
import { FormatUnitPipe } from '../../../pipes/format-unit.pipe';
import { TileV2 } from '../../models/tile-v2.interface';

type TileV2Extension = NonNullable<TileV2['extension']>;
type TileV2ExtensionLine = TileV2Extension[number];

@Component({
  selector: 'pozi-tile-v2',
  imports: [
    RouterLink,
    NgTemplateOutlet,
    FormatDatePipe,
    FormatNumberPipe,
    FormatUnitPipe,
    GoogleMapsComponent,
    ScrollFadeDirective,
    SymbolComponent,
    IconComponent,
    OverlayDirective,
  ],
  templateUrl: './tile-v2.component.html',
  styleUrl: './tile-v2.component.scss',
  host: {
    // Lets the report's scoped styles reach this grid item and its inner grid
    // column so long extension values can be ellipsised (see `.scss`).
    '[class.limit-extension]': 'limitExtension()',
  },
})
export class TileV2Component {
  tile = input.required<TileV2>();

  /**
   * When true, the extension shows only the first few lines and does not scroll.
   */
  limitExtension = input<boolean>(false);

  /**
   * When true and the tile has a navigation target, the whole tile is a single
   * link (covering its full area) and the corner action arrow is not rendered.
   * Renders a real `<a href>` so the link is preserved when exported to PDF.
   */
  clickableTile = input<boolean>(false);

  private readonly limitedExtensionLineCount = 3;

  /**
   * Extension lines to render. When limited and there are more lines than the
   * cap, the first few are shown followed by a generated "{hiddenCount} more" line.
   */
  displayedExtension = computed(() => {
    const extension = this.tile().extension;
    if (
      !extension ||
      !this.limitExtension() ||
      extension.length <= this.limitedExtensionLineCount
    ) {
      return extension;
    }

    const shown = extension.slice(0, this.limitedExtensionLineCount);
    const hiddenCount = extension.length - shown.length;
    return [...shown, this.buildMoreLine(extension[0], hiddenCount)];
  });

  progressBar = computed(() => {
    const progressBar = this.tile().progressBar;
    if (!progressBar) {
      return [];
    }

    return Array.isArray(progressBar) ? progressBar : [progressBar];
  });

  columnGridTemplateRows = computed<string>(() => {
    const { secondaryValues, progressBar } = this.tile();
    const progressBarHeight = progressBar ? this.layout.progressBarHeight : 0;
    const rows = [
      `minmax(${this.layout.headerHeight - progressBarHeight}px, max-content)`,
      progressBar ? 'max-content' : null,
      secondaryValues?.length ? 'max-content' : null,
    ];

    return rows.filter(Boolean).join(' ');
  });

  private readonly layout = {
    tileHeight: 120, // $tile-v2-height
    paddingY: 8,
    secondaryValuesHeight: 41,
    progressBarHeight: 8,
    get headerHeight() {
      return this.tileHeight - 2 * this.paddingY - this.secondaryValuesHeight;
    },
  } as const;

  readonly nonInteractiveGoogleMapsOptions =
    NON_INTERACTIVE_GOOGLE_MAPS_OPTIONS;
  readonly cdkOverlayPositionPairs = defaultDropdownPositionPairs;

  /**
   * Builds the trailing "{hiddenCount} more" extension line. Its length mirrors the real
   * lines: when those lead with a symbol, the same symbol is reused as the first
   * element; the last element is a text-only value reading "{hiddenCount} more".
   *
   * @param sampleLine A real extension line used to mirror the symbol/length.
   * @param hiddenCount Number of extension lines not shown.
   */
  private buildMoreLine(
    sampleLine: TileV2ExtensionLine,
    hiddenCount: number
  ): TileV2ExtensionLine {
    const moreValue = {
      text: $localize`:@@tile.extension.more:${hiddenCount}:count: more`,
      color: '#8CA0B4',
    };

    const leading = sampleLine?.[0];
    const hasLeadingSymbol = !!leading && 'symbol' in leading;

    return hasLeadingSymbol ? [leading, moreValue] : [moreValue];
  }
}
